/**
 * FeedbackSettingsCard - Häufigkeit der In-App-Feedback-Frage nach dem Training.
 * Standard: höchstens alle 60 Tage. Kann komplett deaktiviert werden.
 */
import React, { useState, useEffect } from 'react';
import { Star, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeedbackSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/settings/feedback`, authHeaders());
        setSettings(res.data);
      } catch (e) {
        console.error('Error loading feedback settings:', e);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/settings/feedback`, {
        enabled: settings.enabled,
        interval_days: Number(settings.interval_days) || 60,
      }, authHeaders());
      setSettings(res.data);
      toast.success('Feedback-Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <Card className="mb-6 border-amber-200" data-testid="feedback-settings-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Termin-Feedback nach dem Training
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Nach einem abgeschlossenen Training fragt die App den Kunden einmalig nach einer
              Bewertung. Hier legst du fest, wie oft das maximal passieren darf.
            </p>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(checked) => set({ enabled: !!checked })}
            data-testid="feedback-settings-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="interval_days">Frühestens wieder nach ... Tagen</Label>
          <Input
            id="interval_days"
            data-testid="feedback-interval-days"
            type="number"
            min="1"
            value={settings.interval_days ?? 60}
            onChange={(e) => set({ interval_days: e.target.value })}
            className="w-32"
            disabled={!settings.enabled}
          />
          <p className="text-xs text-gray-500">
            Ein Kunde wird höchstens alle {Number(settings.interval_days) || 60} Tage um Feedback
            gebeten – egal wie oft er trainiert. Empfohlen: 60 Tage.
          </p>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="feedback-settings-save-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedbackSettingsCard;
